import { CellContext } from "@/lib/Components/DataTable/types";
import {
  RelocationTableState,
  defaultRelocationTableState,
} from "@/app/Relocations/Utils/relocationTableState";
import {
  RelocationListQueryVariables,
  RelocationStatus,
  RelocationType,
} from "@/gql/graphql";
import {
  getRelocationTableVariables,
  RelocationColumnId,
  relocationTableDefaultSorting,
} from "@/app/Dashboard/Utils/getRelocationTableVariables";
import { ReactNode, useState } from "react";
import { useTranslations } from "use-intl";
import { RelocationStatusCell } from "@/app/Relocations/Components/RelocationStatusCell";
import { formatDate } from "@/lib/Formatters/formatDate";
import {
  RelocationListItem,
  relocationListQuery,
} from "@/app/Relocations/GraphQL/relocationListQuery";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import {
  MultiSelectFilter,
  MultiSelectFilterOption,
} from "@/lib/Components/DataTable/Filters/MultiSelectFilter";
import { TimeSinceNow } from "@/lib/Components/Common/TimeSinceNow";
import { useGapRentalActions } from "@/app/GapRentals/Hooks/useGapRentalActions";
import type { TableId } from "@/app/Common/Utils/table-id";

import { Button } from "@/components/catalyst/button";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { Checkbox } from "@/components/catalyst/checkbox";
import { toast } from "sonner";
import { RegionFilter } from "@/app/Common/Components/RegionFilter";
import { CityFilter } from "@/lib/Components/DataTable/Filters/CityFilter";
import { SupplierFilter } from "@/lib/Components/DataTable/Filters/SupplierFilter";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { GapRentalInfoDialog } from "@/app/GapRentals/Components/GapRentalInfoDialog";
import { GapRentalGraphic } from "@/app/GapRentals/Components/GapRentalGraphic";
import { GapRentalDealCell } from "@/app/GapRentals/Components/GapRentalDealCell";
import { updateRelocationStatuses } from "@/app/Relocations/GraphQL/relocationMutations";
import { RelocationReferenceCell } from "@/app/Relocations/Components/Cells/RelocationReferenceCell";
import { Link } from "@/components/catalyst/link";
import { Highlighter } from "@/lib/Components/Highlighter";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { ArchiveBoxArrowDownIcon } from "@heroicons/react/20/solid";
import { Star } from "lucide-react";
import {
  ExpiresCell,
  QuantityCell,
  VehicleCell,
} from "@/app/Relocations/Components/RelocationTable";

function getStatusOptions(
  t: (key: string) => string,
): MultiSelectFilterOption<RelocationStatus>[] {
  return [
    { label: t("statusLabels.READY"), value: RelocationStatus.Ready },
    { label: t("statusLabels.SOLD_OUT"), value: RelocationStatus.SoldOut },
    { label: t("statusLabels.PAUSED"), value: RelocationStatus.Paused },
    { label: t("statusLabels.EXPIRED"), value: RelocationStatus.Expired },
    { label: t("statusLabels.ARCHIVED"), value: RelocationStatus.Archived },
    { label: t("statusLabels.DRAFT"), value: RelocationStatus.Draft },
  ];
}

type GapRentalTableProps = {
  title?: string;
  rightButtons?: ReactNode;
  id: TableId;
  queryVariables?: Partial<RelocationListQueryVariables>;
  hiddenColumns?: RelocationColumnId[];
  searchable?: boolean;
  supplierId?: string;

  //Un-controlled component
  defaultState?: Partial<RelocationTableState>;

  //Controlled component
  state?: RelocationTableState;
  onStateChange?: (state: RelocationTableState) => void;
};

export function GapRentalTable({
  title,
  rightButtons,
  id,
  defaultState,
  queryVariables,
  hiddenColumns,
  state: stateProp,
  onStateChange,
  searchable,
  supplierId: supplierIdProp,
}: GapRentalTableProps) {
  "use no memo";
  const { supplierId: globalSupplierId } = useGlobalSupplier();
  const getActions = useGapRentalActions();
  const { open } = useConfirmDeleteDialog();
  const { mutateAsync } = useGqlMutation(updateRelocationStatuses);

  const [localState, setLocalState] = useState<RelocationTableState>({
    ...defaultRelocationTableState,
    ...defaultState,
  });

  const state = stateProp ?? localState;

  // Any change other than paging puts the user back on the first page.
  const updateState = (patch: Partial<RelocationTableState>) => {
    const next = { ...state, page: 1, ...patch };

    if (onStateChange) {
      onStateChange(next);
    } else {
      setLocalState(next);
    }
  };

  // Gap rentals start and end at the same office, so the single location
  // filter drives the departure city.
  const {
    statuses: status,
    regions,
    departureCityIds: locationCityIds,
  } = state;

  const scopedSupplierId = supplierIdProp ?? globalSupplierId;
  const supplierId = scopedSupplierId ?? state.supplierId;
  const t = useTranslations("relocation");
  const tTable = useTranslations("relocation.table");

  const columns: DataTableColDef<
    RelocationListItem,
    unknown,
    RelocationColumnId
  >[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            table.getIsSomePageRowsSelected()
          }
          color="indigo"
          onChange={(value) => table.toggleAllPageRowsSelected(value)}
          aria-label={tTable("selectAll")}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          color="indigo"
          disabled={row.original.status === RelocationStatus.Archived}
          checked={row.getIsSelected()}
          onChange={(value) => row.toggleSelected(value)}
          aria-label={tTable("selectRow")}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "status",
      cell: (ctx) => <RelocationStatusCell {...ctx} hideDriverRequest />,
      accessorFn: (model) => model,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("status")} />
      ),
    },
    {
      id: "relocation",
      accessorFn: (model) => model,
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("location")} />
      ),
      cell: GapRentalLocationCell,
    },
    {
      id: "vehicle",
      accessorFn: (model) => model.vehicle.name,
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("vehicle")} />
      ),
      cell: VehicleCell,
    },
    {
      id: "line",
      header: tTable("plate"),
      cell: RelocationReferenceCell,
      accessorFn: (model) => model,
    },
    {
      id: "deal",
      header: tTable("deal"),
      accessorFn: (model) => model,
      cell: GapRentalDealCell,
    },
    {
      id: "bookings",
      header: tTable("bookings"),
      accessorFn: (model) => model.count,
      cell: QuantityCell,
    },
    {
      id: "expires_at",
      accessorFn: (model) => model,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("expires")} />
      ),
      cell: ExpiresCell,
    },
    {
      id: "created_at",
      accessorFn: (model) => model,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("created")} />
      ),
      cell: ({ row }) => <TimeSinceNow time={row.original.created_at} />,
    },
  ];

  return (
    <DataTable
      id={id}
      searchable={searchable}
      page={state.page}
      onPageChange={(page) => updateState({ page })}
      search={state.search}
      onSearchChange={(search) => updateState({ search })}
      hiddenColumns={hiddenColumns}
      getActions={getActions}
      initialSorting={relocationTableDefaultSorting}
      filters={
        <div className="flex flex-wrap gap-2">
          <MultiSelectFilter
            label={tTable("status")}
            options={getStatusOptions(t)}
            selected={status}
            onChange={(statuses) => updateState({ statuses })}
          />

          {scopedSupplierId ? null : (
            <SupplierFilter
              label={tTable("supplier")}
              selected={state.supplierId}
              onChange={(supplierId) => updateState({ supplierId })}
            />
          )}

          <RegionFilter
            selected={regions}
            onChange={(regions) => updateState({ regions })}
          />

          <CityFilter
            placeholder={tTable("location")}
            selected={locationCityIds}
            regions={regions}
            onChange={(departureCityIds) => updateState({ departureCityIds })}
          />
        </div>
      }
      title={title}
      rightButtons={(table) => {
        const selected = table.getSelectedRowModel().rows;

        return selected.length > 0 ? (
          <div className="flex h-9 items-center space-x-3 rounded-lg border border-blue-200 bg-blue-50 pl-4 shadow-sm">
            <div className="text-sm font-medium text-blue-700">
              {tTable("selected", { count: selected.length })}
            </div>

            <Button
              plain
              className="text-sm text-blue-600 underline decoration-dashed underline-offset-2 hover:text-blue-800"
              onClick={() => {
                table.toggleAllPageRowsSelected(false);
              }}
            >
              {tTable("deselectAll")}
            </Button>

            <Button
              onClick={() => {
                open({
                  title: tTable("archiveTitle"),
                  message: tTable("archiveMessage"),
                  buttonText: tTable("archiveButton"),
                  onDelete: async () => {
                    await mutateAsync({
                      ids: selected.map((row) => row.original.id),
                      status: RelocationStatus.Archived,
                    });

                    table.toggleAllPageRowsSelected(false);

                    toast.success(
                      tTable("archiveSuccess", { count: selected.length }),
                    );
                  },
                });
              }}
            >
              <ArchiveBoxArrowDownIcon />
              {tTable("archive")}
            </Button>
          </div>
        ) : (
          rightButtons
        );
      }}
      columns={columns}
      emptyState={<GapRentalEmptyState />}
      document={relocationListQuery}
      accessor={(data) => data.relocations}
      getQueryVariables={getRelocationTableVariables({
        queryVariables,
        status,
        type: [RelocationType.GapRental],
        regions,
        departureCityIds: locationCityIds,
        supplierId,
      })}
    />
  );
}

function GapRentalEmptyState() {
  const tInfo = useTranslations("relocation.gapRentalInfo");
  const { open } = useDialog(GapRentalInfoDialog);

  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-6 text-center">
      <div className="w-full max-w-sm">
        <GapRentalGraphic />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-900">
          {tInfo("emptyTitle")}
        </p>
        <p className="mt-1 text-xs text-gray-500">{tInfo("subtitle")}</p>
      </div>
      <Button outline onClick={() => open({})}>
        {tInfo("emptyAction")}
      </Button>
    </div>
  );
}

function GapRentalLocationCell({
  table,
  row,
}: CellContext<RelocationListItem, any>) {
  "use no memo";
  const { hasSupplier } = useGlobalSupplier();
  const search = table.options.meta?.search ?? "";
  const relocation = row.original;

  const imageSource = relocation.ogImage ?? relocation.vehicle.heroImage;

  return (
    <div className="flex min-w-0 space-x-3">
      {imageSource ? (
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg">
          {relocation.ogImage ? (
            <ImoovaImage
              alt={relocation.reference}
              media={relocation.ogImage}
              className="h-full w-full object-cover"
            />
          ) : (
            <img
              alt={relocation.vehicle.name}
              src={relocation.vehicle.heroImage}
              className="h-full w-full object-contain"
            />
          )}
        </div>
      ) : null}

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-1.5">
          <Link
            className="block truncate font-medium text-blue-600 hover:text-blue-800"
            from="/$tenant/$orgId/supplier/{-$supplierId}"
            to="/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals/$relocationId"
            params={(prev) => ({
              ...prev,
              relocationId: relocation.id,
            })}
            viewTransition
          >
            <Highlighter searchString={search}>
              {relocation.reference}
            </Highlighter>
          </Link>
          {relocation.priority !== null && relocation.priority !== undefined ? (
            <Star className="h-4 w-4 shrink-0 fill-yellow-400 text-yellow-400" />
          ) : null}
        </div>

        <div className="flex min-w-0 items-center text-sm">
          <span className="truncate font-medium text-gray-900">
            <Highlighter searchString={search}>
              {relocation.departureOffice.name}
            </Highlighter>
          </span>
        </div>

        {!hasSupplier ? (
          <p className="truncate text-xs text-gray-600">
            <Highlighter searchString={search}>
              {relocation.supplier.name}
            </Highlighter>
          </p>
        ) : null}

        <p className="text-xs text-gray-500">
          {formatDate(relocation.available_from_date)} -{" "}
          {formatDate(relocation.available_to_date)}
        </p>
      </div>
    </div>
  );
}
